import { Module } from 'vuex'
import { StateInterface } from '@/store'
import { CtTask, TaskType } from '@/models/interfaces/content-type'
import { SelectionDict, UniqKey } from '@/models/generics'
import { $castArray, $get, $set, isUUID } from '@/utils'
import { Status } from '@/models/status'
import { ContentType } from '@/services/directr'

type Task = CtTask

type TaskDict = { [Property in keyof TaskType]?: Record<string, Task> }
type TaskSelectionDict = { [Property in keyof TaskType]?: Record<string, SelectionDict> }

export interface TaskState {
  tasks: TaskDict,
  selections: TaskSelectionDict
}

const DEFAULT_TASK_ID_PATH = 'sys.id'

const TasksModule: Module<TaskState, StateInterface> = {
  namespaced: true,
  state: {
    tasks: {},
    selections: {}
  },
  mutations: {
    SetTasks: (state, { tasks, path, type }: { tasks: Array<Task | Record<string, any>>, path: string, type: TaskType }) => {
      const idPath = path || DEFAULT_TASK_ID_PATH
      tasks.forEach((task) => {
        if (typeof $get(task, idPath) !== 'string' || $get<any>(task, idPath) === '') throw Error(`Tried to add new task of type "${type || task.type || TaskType.Default}", but task field value at key "${idPath}" was not a string or empty: \n${task && typeof task === 'object' ? JSON.stringify(task) : String(task)}`)

        $set(state.tasks, [type !== undefined ? type : Object.values(TaskType).includes(task.type) ? task.type : TaskType.Default, $get(task, idPath)], task)
        if (task.selection) $set(state.selections, [type !== undefined ? type : Object.values(TaskType).includes(task.type) ? task.type : TaskType.Default, $get(task, idPath)], task.selection)
      })
    },
    ClearAll: (state, { type }: { type: TaskType }) => {
      if (Object.keys(state.tasks[type] || {}).length)
        Object.keys(state.tasks[type]).forEach(key => delete state.tasks[type][key])
      // state.tasks[type] = {}
    },
    ClearTask: (state, { task, type }: { task: UniqKey, type: TaskType }) => {
      if (!state.tasks[type]) {
        console.warn(`Could not clear task ${task} of type ${type}. state.tasks does not contain a key for type "${type}"`)
        return
      }
      delete state.tasks[type][task]
    },
    SetTaskCompleted: (state, { task, type }: { task: UniqKey, type: TaskType }) => {
      if (!state.tasks[type]) throw Error(`Tried so complete task of type ${type} by id ${task}, but no task list for the type exists`)
      if (state.tasks[type] && !state.tasks[type][task]) throw Error(`Tried so complete task of type ${type} by id ${task}, but it does not exist`)
      state.tasks[type][task].status = Status.Final
      // state.tasks[type][task] = {...state.tasks[type][task], status: Status.Final}
    },
    SetSelection: (state, { task, type, selection }: { task: string, type: TaskType, selection: SelectionDict }) => {
      // console.log('ss', task, type, selection)
      if (!state.selections[type]) state.selections[type] = {}
      state.selections[type][task] = selection
    },
    RemoveTask: (state, { type, task }: { type: TaskType, task: UniqKey }) => {
      if (!state.tasks[type]) throw Error(`Tried to remove task of type ${type} (task ${task}), but no key for that type exists`)
      delete state.tasks[type][task]
    }
  },
  getters: {
    Dict: state => state.tasks,
    Selections: state => state.selections,
    Count: state => Object.keys(state).reduce((sum, key) => sum + (Object.keys(state[key]).length || 0), 0)
  },
  actions: {
    Clear ({ state, commit, rootGetters }, { type }) {
      commit('ClearAll', { type })
      return state
    },
    async Fetch ({ state, commit, rootGetters }, { type, pending, query, created_by, replace }) {
      // console.log('fetching', type, pending, query)
      const q = query || [
        { field: 'type', method: 'is', match: type },
        ...(pending
          ? [{ field: 'status', method: 'nin', match: 'final' },
          { field: 'status', method: 'nin', match: 'archived' }]
          : [{}])]

      if (isUUID(created_by)) q.push({ field: 'sys.created_by', method: 'is', match: created_by })

      const { sys, items } = await rootGetters['Services/Directr'].tasks
        .list(q)

      if (replace) commit('ClearAll', { type })
      commit('SetTasks', { tasks: items })
      return state
    },
    async Get ({ state, commit, rootGetters }, id) {
      const { sys, items } = await rootGetters['Services/Directr'].tasks.get(id)
      // console.log('get', items)
      commit('SetTasks', { tasks: items })
      return state
    },
    async Add ({ commit, rootGetters }, { type, task }: { type: TaskType, task: Task, path: string | Array<string> }) {
      const { items: created } = await rootGetters['Services/Directr'].tasks.create(task)
      // console.log('add', created)
      commit('SetTasks', { tasks: created })
      return created
    },
    Remove ({ state, commit }, { type, task, path }: { type: TaskType, task: Task | Array<Task>, path: string | Array<string> }) {
      try {
        commit('RemoveTask', { type, task })
      } catch (e) {
        console.warn(`Failed to remove task "${task}"`, e)
      }
    },
    AddLocal ({ state, commit }, { type, task, path, diff }: { type: TaskType, task: Task | Array<Task>, path: string | Array<string>, diff: boolean }) {
      const tasks = Array.isArray(task) ? task : $castArray(task)

      // diff against current value & remove any tasks not in the new set
      if (diff) {
        if (!state.tasks[type]) commit('ClearAll', { type })
        const newKeys: Array<string> = $castArray(task).map(t => $get(t, path || DEFAULT_TASK_ID_PATH))
        const removedKeys = Object.keys(state.tasks[type] || {}).filter(key => Boolean(key) && !newKeys.includes(key))

        commit('SetTasks', { tasks, type, path })
        // remove all keys not in the new set
        // emit a warning if removal fails as to not chrash the entire app...

        // console.log('AddLocal', type, tasks, removedKeys)
        try {
          if (task && removedKeys?.length) removedKeys.forEach(t => commit('RemoveTask', { type, task: t }))
        } catch (e) {
          console.warn('Error while trying to remove task', e)
        }

        return state.tasks[type]
      }

      commit('SetTasks', { tasks, type, path })

      return state.tasks[type]
    },
    async Update ({ commit, rootGetters }, { type, task }: { type: TaskType, task: Task }) {
      // console.log('update', task)
      const { items: updated } = await rootGetters['Services/Directr'].tasks.update(task?.sys?.id, task)
      commit('SetTasks', { tasks: updated })
      return updated
    },

    async Cancel ({ commit, rootGetters }, { type, task }: { type: TaskType, task: UniqKey }) {
      // console.log('cancel', task)
      const { items: removed } = await rootGetters['Services/Directr'].tasks.delete(task)
      commit('RemoveItems', { type: removed[0]?.type, task: removed[0]?.sys.id })
      return removed
    },

    SetSelection ({ commit }, { type, task, selection }: { type: TaskType, task: UniqKey, selection: SelectionDict }) {
      // console.log('setSelection', task, selection)
      // replace selection dict by task type -> task id
      commit('SetSelection', { type, task, selection })
    },
    async AddSelection ({ rootGetters, state, commit }, { type, task, selection, patch }: { type: TaskType, task: UniqKey, selection: SelectionDict, patch: boolean }) {
      // console.log('AddSelection', task, {...selection})
      // add to selection dict by task type -> task id
      const current = state.selections[type] ? (state.selections[type][task] || {}) : {}

      if (isUUID(task) && patch === true) {
        const { items: updated } = await rootGetters['Services/Directr'].tasks.selection(task, selection)
        if (!updated?.length) throw Error('tasks.complete returned undefined')
      }

      commit('SetSelection', { type, task, selection: { ...current, ...selection } })
      // console.log('committed', state.selections)

    },
    RemoveSelection ({ state, commit }, { type, task, key }: { type: TaskType, task: UniqKey, key: string }) {
      // console.log('RemoveSelection', task, key)
      const current = state.selections[type] ? state.selections[type][task] : {}
      delete current[key]
      commit('SetSelection', { type, task, selection: current })
    },
    ClearSelection ({ commit }, { type, task }: { type: TaskType, task: UniqKey }) {
      commit('SetSelection', { type, task, selection: {} })
    },

    async Complete ({ commit, rootGetters }, { type, task, clear = false, save = true, clearDelay }: { type: TaskType, task: UniqKey, save: boolean, path: string | Array<string>, clear: boolean, clearDelay?: number }) {
      // console.log('Complete', task)
      if (isUUID(task) && save === true) {
        const { items: updated } = await rootGetters['Services/Directr'].tasks.complete(task)
        if (!updated?.length) throw Error('tasks.complete returned undefined')
      }

      if (clear) {
        if (!clearDelay) commit('ClearTask', { task, type })
        else setTimeout(() => { commit('ClearTask', { task, type }) }, clearDelay)
      }
      else commit('SetTaskCompleted', { type, task })
    }
  }
}

export default TasksModule
